/**
 * `<EmptyState>` — the one empty/zero-data screen used across the dashboard.
 *
 * Wraps the ui-dashboard `Empty` primitives in the experiment design (accent
 * icon tile + serif-free title + muted description + optional action) so every
 * page renders the same thing. Callers pass their own `action` node (a Link or
 * Button) to keep routing/i18n/handlers local.
 *
 * Two positions via `variant`:
 *   - "page" (default): full-height, vertically centered in the page body —
 *     for list pages like Agents and Contacts.
 *   - "card": a self-contained bordered card — for an empty section inside a
 *     larger page (KB sources/jobs, Tools, the retrieval playground).
 */

import { cn } from "@cometchat-ai/ui-dashboard";
import type { LucideIcon } from "./icons";
import type { ReactNode } from "react";

interface EmptyStateProps {
  /** Icon shown in the accent tile. */
  icon: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  /** Optional action(s) — a Button/Link the caller owns. */
  action?: ReactNode;
  /** Accent color for the icon tile. Defaults to the brand accent. */
  accent?: string;
  /** Positioning: full-height page center, or a boxed card. */
  variant?: "page" | "card";
  className?: string;
  "data-testid"?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  variant = "page",
  className,
  ...rest
}: EmptyStateProps) {
  return (
    <div
      data-testid={rest["data-testid"]}
      className={cn(
        "flex flex-col items-center justify-center gap-3 text-center",
        variant === "page"
          ? "flex-1 px-6 py-16"
          : "rounded-xl border border-dashed border-border px-6 py-12",
        className,
      )}
    >
      {/* Final empty-state tile — quiet muted square, not an accent badge. */}
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-foreground">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? (
          <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
